// What bids say about a question's price. See Design > Names and definitions,
// "Pricing signal". Studio recommends a price; it never changes one: a price is
// the intent's ask in the hub manifest, changed by pushing a toolset.

import { activityByIntent, isLive, percentile } from "./activity.ts";
import { normalize } from "./golden.ts";
import type { EventRow, HubSummary } from "./types.ts";

export type PricingSignal = "pricedOut" | "preset" | "room" | "holds" | "quiet";

export type PricingRow = {
  intent: string;
  askPerMillion: number;
  answered: number;
  /** Bids on answered asks, per million answers. */
  answeredBids: number[];
  medianAnsweredBid: number | null;
  /** Bids on asks the price turned away, per million answers, retries included. */
  lostBids: number[];
  /** Lost asks whose question came back at a higher bid and was answered. */
  retried: number;
  /** Bids on asks the price turned away that didn't come back: the demand the price lost. */
  pricedOutBids: number[];
  /** Asks left out because they repeat the hub's examples or golden questions. */
  setAside: number;
  /** Most answered asks bid exactly the same amount, as a preset bid would. */
  uniformBids: boolean;
  signal: PricingSignal;
};

/** How long after a priced-out ask an answer to the same question counts as the consumer trying again. */
export const RETRY_WINDOW_MS = 15 * 60 * 1000;
/** A median bid this many times the price, over at least this many answers, is room to raise it. */
export const ROOM_RATIO = 1.5;
export const ROOM_MIN_ANSWERS = 3;
/** When this share of answered asks bid exactly one amount, the bids look preset, not chosen. */
export const PRESET_BID_SHARE = 0.8;

/** The share of bids that are the most common single amount. */
export function topBidShare(bids: ReadonlyArray<number>): number {
  if (bids.length === 0) return 0;
  const counts = new Map<number, number>();
  for (const b of bids) counts.set(b, (counts.get(b) ?? 0) + 1);
  return Math.max(...counts.values()) / bids.length;
}

/** An event's bid per million answers, as a list of none or one. */
const bidOf = (e: EventRow): number[] => (e.bid_nano === null ? [] : [e.bid_nano / 1000]);

const sameAsk = (a: EventRow, b: EventRow) => a.intent === b.intent && a.question.trim().toLowerCase() === b.question.trim().toLowerCase();

/** Priced-out asks followed, within the window, by an answer to the same question: someone trying again with a higher bid. */
export function retriedAsks(events: ReadonlyArray<EventRow>, windowMs = RETRY_WINDOW_MS): Set<EventRow> {
  const answers = events.filter((e) => !e.test_run && e.outcome === "Answer");
  const out = new Set<EventRow>();
  for (const e of events) {
    if (e.test_run || e.outcome !== "PriceTooLow") continue;
    if (answers.some((a) => sameAsk(a, e) && a.created_at > e.created_at && a.created_at - e.created_at <= windowMs)) out.add(e);
  }
  return out;
}

/** Each live question with its bids, from the hub's newest events. Asks whose text is in `presets` (the hub's
 *  examples and golden questions, which the Console's examples and lessons send) are set aside. */
export function pricingRows(hub: HubSummary, allEvents: ReadonlyArray<EventRow>, presets: ReadonlySet<string> = new Set()): PricingRow[] {
  const isPreset = (e: EventRow) => presets.has(normalize(e.question));
  const events = allEvents.filter((e) => !isPreset(e));
  const setAside = new Map<string, number>();
  for (const e of allEvents) if (e.intent && !e.test_run && isPreset(e) && (e.outcome === "Answer" || e.outcome === "PriceTooLow")) setAside.set(e.intent, (setAside.get(e.intent) ?? 0) + 1);
  const activity = activityByIntent(events);
  const retried = retriedAsks(events);
  return hub.manifest.intents.filter(isLive).map((intent) => {
    const a = activity.get(intent.id);
    const evs = a?.events ?? [];
    const answeredBids = evs.filter((e) => e.outcome === "Answer").flatMap(bidOf);
    const lost = evs.filter((e) => e.outcome === "PriceTooLow" && e.bid_nano !== null);
    const lostBids = lost.flatMap(bidOf);
    const pricedOutBids = lost.filter((e) => !retried.has(e)).flatMap(bidOf);
    const median = percentile(answeredBids, 50);
    const ask = intent.askUsdPerMillion;
    const uniformBids = answeredBids.length >= ROOM_MIN_ANSWERS && topBidShare(answeredBids) >= PRESET_BID_SHARE;
    const signal: PricingSignal = pricedOutBids.length > 0 ? "pricedOut"
      : uniformBids ? "preset"
      : median !== null && answeredBids.length >= ROOM_MIN_ANSWERS && median >= ask * ROOM_RATIO ? "room"
      : (a?.answered ?? 0) > 0 || lostBids.length > 0 ? "holds" : "quiet";
    return { intent: intent.id, askPerMillion: ask, answered: a?.answered ?? 0, answeredBids, medianAnsweredBid: median, lostBids, retried: lost.length - pricedOutBids.length, pricedOutBids, setAside: setAside.get(intent.id) ?? 0, uniformBids, signal };
  });
}

/** The price that would have earned the most in the same events: every answered and priced-out ask whose bid
 *  reaches it pays it. Above `floor` (the fee and inputs), so the margin stays positive. Null when nothing beats
 *  today's price, so Studio only recommends a change that would have earned more. */
export function bestPrice(answeredBids: ReadonlyArray<number>, pricedOutBids: ReadonlyArray<number>, ask: number, floor: number) {
  const bids = [...answeredBids, ...pricedOutBids];
  const todayPerMillion = ask * answeredBids.length;
  let best: { price: number; answers: number; earnedPerMillion: number } | null = null;
  for (const price of [...new Set(bids)].filter((p) => p > floor && p !== ask).sort((a, b) => a - b)) {
    const answers = bids.filter((b) => b >= price).length;
    const earnedPerMillion = answers * price;
    if (earnedPerMillion > (best?.earnedPerMillion ?? todayPerMillion)) best = { price, answers, earnedPerMillion };
  }
  return best ? { ...best, todayPerMillion, direction: best.price < ask ? "lower" as const : "raise" as const } : null;
}
